import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { selectAuthSession } from '../../auth/store/selectors';
import { DEFAULT_AVATAR_ID } from '../../constants';
import { fetchCourseUserProfiles } from '../../db/user-profiles/actions';
import { selectCourseUserProfile } from '../../db/user-profiles/selectors';
import { useAppSelector } from '../../store/hooks';
import { CourseRole } from '../../types';
import { getNameInitials } from '../../utils/name';

interface CourseRoleRequired {
  isCourseRoleNeeded: true;
  existingCourseRole: CourseRole | undefined;
}

interface CourseRoleOptional {
  isCourseRoleNeeded?: false;
}

export type UseCourseUserAvatar = {
  avatarId: MongoId | typeof DEFAULT_AVATAR_ID;
  bucketUrl?: BucketURL;
  /** If there is an error loading the avatar image, first letter of the name would be displayed */
  name?: string;
  /** @default false */
  isEmailIdNeeded?: boolean;
} & (CourseRoleRequired | CourseRoleOptional);

const useCourseUserAvatar = ({
  avatarId,
  bucketUrl,
  name,
  isEmailIdNeeded = false,
  ...props
}: UseCourseUserAvatar) => {
  const dispatch = useDispatch();

  const [loaded, setLoaded] = useState(false);

  const session = useAppSelector((state) => selectAuthSession(state));

  const courseUserProfile = useAppSelector((state) => selectCourseUserProfile(state, avatarId || ''));
  const { avatarUrl } = courseUserProfile || {};

  const nameInitials = useMemo(() => {
    if (avatarId) return;
    if (!name) return;
    return getNameInitials(name).charAt(0);
  }, [avatarId, name]);

  const handleLoad = useCallback(() => setLoaded(true), []);

  const isCourseRoleNeeded = props.isCourseRoleNeeded ?? false;
  const existingCourseRole = props.isCourseRoleNeeded ? props.existingCourseRole : undefined;

  useEffect(() => {
    if (!avatarId || avatarId === DEFAULT_AVATAR_ID) return;
    dispatch(
      fetchCourseUserProfiles({
        avatarId,
        isEmailIdNeeded,
        isCourseRoleNeeded,
        existingCourseRole,
      })
    );
  }, [
    dispatch,
    avatarId,
    bucketUrl,
    session?.s3BucketURL,
    isEmailIdNeeded,
    isCourseRoleNeeded,
    existingCourseRole,
  ]);

  return {
    loaded,
    nameInitials,
    handleLoad,
    avatarUrl,
    session,
  };
};

export default useCourseUserAvatar;
